<template>
  <v-navigation-drawer
    fixed
    right
    temporary
    floating
    permanent
    :width="width"
    :class="secondaryDrawer ? 'secondary-drawer' : ''"
  >
    <v-btn title="Close" @click="exit()" text fab small>
      <v-icon dark>$close</v-icon>
    </v-btn>
    <div class="app-content--equal-padding pa-10">
      <header class="mb-8">
        <v-card-title
          class="headline font-weight-bold mb-1 pa-0 letter-spacing-compact"
        >
          Your Profile
        </v-card-title>
        <p
          class="block mb-1 caption grey--text lighten-5 letter-spacing-compact"
        >
          Edit Your Information
        </p>
        <hr />
      </header>
      <div>
        <validation-observer ref="validator">
          <v-form @submit.prevent="submit()">
            <DropZone
              ref="avatar"
              label="Upload logo"
              :remote-files="currentUser.media.photos"
              :subjectId="currentUser.id"
              subjectType="users"
              category="photos"
              :allowMultiple="false"
              class="mb-6"
            />
            <ValidationProvider
              name="first_name"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="first_name"
                :error-messages="errors"
                v-model="request.first_name"
                label="First Name"
                required
                class="mb-4"
              />
            </ValidationProvider>
            <ValidationProvider
              name="last_name"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="last_name"
                :error-messages="errors"
                v-model="request.last_name"
                label="Last Name"
                required
                class="mb-4"
              />
            </ValidationProvider>
            <ValidationProvider
              name="phone_number"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="phone_number"
                type="phone_number"
                :error-messages="errors"
                v-model="request.phone_number"
                label="Phone Number"
                required
                class="mb-4"
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="request.hasOwnProperty('bank_name')"
              name="bank_name"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="bank_name"
                :error-messages="errors"
                v-model="request.bank_name"
                label="Bank"
                required
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="request.hasOwnProperty('bank_account_number')"
              name="bank_account_number"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="bank_account_number"
                :error-messages="errors"
                v-model="request.bank_account_number"
                label="Account Number"
                required
              />
            </ValidationProvider>

            <CountryField
              v-if="request.hasOwnProperty('country_code')"
              name="country"
              rules="required"
              label="Country"
              item-value="alpha2Code"
              v-model="request.country_code"
            />
            <ValidationProvider
              v-if="request.hasOwnProperty('years_of_diving')"
              name="diving_years"
              rules="required|integer"
              v-slot="{ errors }"
            >
              <v-text-field
                name="years_of_diving"
                :error-messages="errors"
                v-model="request.years_of_diving"
                label="Years of diving"
                required
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="request.hasOwnProperty('no_of_dives')"
              name="diving_number"
              rules="required|integer"
              v-slot="{ errors }"
            >
              <v-text-field
                name="no_of_dives"
                :error-messages="errors"
                v-model="request.no_of_dives"
                label="Number of dives"
                required
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="request.hasOwnProperty('business')"
              name="company_name"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                name="business_name"
                :error-messages="errors"
                v-model="request.business.name"
                label="Company Name"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="request.hasOwnProperty('business')"
              name="business_website"
              v-slot="{ errors }"
            >
              <v-text-field
                name="business_website"
                website="business_website"
                :error-messages="errors"
                v-model="request.business.website"
                label="Company Website"
              />
            </ValidationProvider>

            <LocationField
              v-if="request.hasOwnProperty('business')"
              name="business_location"
              v-model="request.business.location"
              :required="true"
            />
            <div v-if="request.hasOwnProperty('business')">
              <label class="d-block mt-6 mb-8 grey--text">
                Type of trips (Select all that apply)
              </label>
              <v-chip
                v-for="(trip, index) in trips"
                :key="index"
                :class="
                  request.business.trip_ids.includes(trip.id)
                    ? 'black white--text'
                    : ''
                "
                :outlined="!request.business.trip_ids.includes(trip.id)"
                class="mr-6 mb-6 px-7 py-4 body-1 border-radius-5 h-40 black"
                @click="toggleTrip(trip.id)"
                height="40px"
              >
                {{ trip.name }}
              </v-chip>

              <validation-provider
                class="mt-12"
                name="trips"
                rules="required"
                v-slot="{ errors }"
              >
                <input
                  type="hidden"
                  name="trips[]"
                  v-model="request.business.trip_ids"
                />
                <div
                  v-if="errors.length && request.business.trip_ids.length < 1"
                  class="v-messages theme--light error--text"
                  role="alert"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      Please select at least one
                    </div>
                  </div>
                </div>
              </validation-provider>
            </div>
            <div class="action-button-drawer">
              <v-btn
                large
                block
                color="white"
                class="elevation-1 mr-3 font-weight-bold mb-3"
                @click="exit()"
              >
                Cancel
              </v-btn>
              <v-btn
                large
                block
                color="primary"
                class="elevation-1 font-weight-bold"
                @click.prevent="submit()"
                :disabled="request.submitting"
              >
                Done
              </v-btn>
            </div>
          </v-form>
        </validation-observer>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import UserRequest from '@request/UserRequest';
import userCrud from '@mixins/userCrud';
import CountryField from '../../components/utilities/CountryField.vue';
import LocationField from '../../components/utilities/LocationField.vue';

export default {
  name: 'Profile',
  mixins: [userCrud],
  props: {
    isDrawer: {
      type: Boolean,
      default: false,
    },
    isRouteView: {
      type: Boolean,
      default: false,
    },
    secondaryDrawer: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [String, Number],
      default: 500,
    },
  },
  components: {
    DropZone: () => import('@components/utilities/DropZone'),
    CountryField,
    LocationField,
  },
  data() {
    return {
      request: new UserRequest(this.$refs, this.$store.state.auth.user),
    };
  },
  methods: {
    async submit() {
      if (await this.$refs.validator.validate()) {
        let response = await this.request.submit();
        if (response) {
          await this.$refs.avatar.upload();
          await this.$store.dispatch('auth/fetchUser');
          this.request.reset();
          this.exit();
        }
      }
    },
    exit() {
      this.$emit('exit');
    },
  },
  mounted() {
    if (this.currentUser.isTourismOperator()) {
      this.getTrips();
    }
  },
};
</script>
